import {
    StreamedStateByStreamKey,
    StreamState,
} from 'streamsState/store/streamedData/StreamedDataState';
import { StreamElementDataMessage } from 'communication/types/StreamElement';
import {
    StreamAction,
    StreamActionType,
    StreamMessageReceiveBatchPayload,
    StreamMessageReceivePayload,
} from 'streamsState/store/streamedData/actions';
import { ChannelDestination } from 'communication/Channel';
import { Reducer } from '@reduxjs/toolkit';
import { ensureInvalidStreamMessage } from 'widgets/store/stream/reducer';
import cloneDeep from 'lodash/cloneDeep';

export function createByStreamKeyReducer<
    TStreamDataState,
    TDataElement,
    TStreamState extends StreamState<TStreamDataState>,
>(
    channel: ChannelDestination,
    initialStreamState: TStreamState,
    processDataStreamMessage: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => void
): Reducer<
    StreamedStateByStreamKey<TStreamDataState, TStreamState>,
    StreamAction<TDataElement>
> {
    return (
        state: StreamedStateByStreamKey<TStreamDataState, TStreamState> = {},
        action: StreamAction<TDataElement>
    ): StreamedStateByStreamKey<TStreamDataState, TStreamState> => {
        if (action.payload?.channel !== channel) {
            return state;
        }

        const nextState: StreamedStateByStreamKey<
            TStreamDataState,
            TStreamState
        > = cloneDeep(state);

        const streamKey = action.payload?.streamKey;
        if (streamKey) {
            nextState[streamKey] = nextState[streamKey]
                ? { ...nextState[streamKey] }
                : { ...initialStreamState };
        }

        switch (action.type) {
            case StreamActionType.STREAM_CLEAR_STREAM_DATA: {
                nextState[streamKey] = cloneDeep(initialStreamState);
                return nextState;
            }
            case StreamActionType.STREAM_REMOVE_STREAM_DATA: {
                delete nextState[action.payload!.streamKey];
                return nextState;
            }
            case StreamActionType.STREAM_SET_STREAM_DATA: {
                /*
                 * Replaces the stream data completely. You will not normally use this. It was added for the support visualisation tool
                 */
                nextState[action.payload!.streamKey] = {
                    ...initialStreamState,
                    data: action.payload!.data,
                };
                return nextState;
            }
            case StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE_BATCH: {
                if (
                    !action.payload ||
                    !action.payload.messages ||
                    action.payload.messages.length === 0
                ) {
                    return state;
                }

                nextState[action.payload!.streamKey] =
                    handleStreamMessageReceiveBatch(
                        nextState[action.payload!.streamKey],
                        action.payload!,
                        processDataStreamMessage
                    );

                return nextState;
            }
            case StreamActionType.STREAM_RECEIVE_STREAM_MESSAGE: {
                if (!action.payload || !action.payload.message) {
                    return state;
                }

                nextState[action.payload!.streamKey] =
                    handleStreamMessageReceive(
                        nextState[action.payload!.streamKey],
                        action.payload!,
                        processDataStreamMessage
                    );

                return nextState;
            }
            case StreamActionType.STREAM_HANDLE_STREAM_ERROR: {
                if (!action.payload || !action.payload.error) {
                    return state;
                }
                nextState[action.payload!.streamKey].messages =
                    ensureInvalidStreamMessage(
                        nextState[action.payload!.streamKey].messages,
                        action.payload!.error
                    );
                return nextState;
            }
        }

        return state;
    };
}

function handleStreamMessageReceiveBatch<
    TStreamDataState,
    TDataElement,
    TStreamState extends StreamState<TStreamDataState>,
>(
    state: TStreamState,
    payload: StreamMessageReceiveBatchPayload<TDataElement>,
    processDataStreamMessage: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => void
): TStreamState {
    const nextState: TStreamState = {
        ...state,
        data: cloneDeep(state.data),
    };
    for (const message of payload.messages) {
        processDataStreamMessage(message, nextState);
    }
    return nextState;
}

function handleStreamMessageReceive<
    TStreamDataState,
    TDataElement,
    TStreamState extends StreamState<TStreamDataState>,
>(
    state: TStreamState,
    payload: StreamMessageReceivePayload<TDataElement>,
    processDataStreamMessage: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => void
): TStreamState {
    const nextState: TStreamState = {
        ...state,
        data: cloneDeep(state.data),
    };

    processDataStreamMessage(payload.message, nextState);
    return nextState;
}

export function createProcessStreamByStreamKeyDataMessage<
    TStreamDataState,
    TDataElement,
    TStreamState extends StreamState<TStreamDataState>,
>(
    processDataStreamElement: (
        streamDataElement: TDataElement,
        nextState: TStreamState
    ) => void,
    additionalReducer: (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => void = () => {
        return;
    }
) {
    return (
        streamDataElement: StreamElementDataMessage<TDataElement>,
        nextState: TStreamState
    ) => {
        if (streamDataElement.data) {
            for (const element of streamDataElement.data) {
                processDataStreamElement(element, nextState);
            }
        }
        additionalReducer(streamDataElement, nextState);
    };
}
